/**
 * PORTAL RH ACEROS LEAL — SIGRH v3 FINAL
 * - Logo: SIGRH
 * - Hora AM/PM
 * - Sin gráfica distribución
 * - Botones funcionales
 * - Login real con Google OAuth + validación de dominio @acerosleal.com
 */

import React, { useState, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { API_URL, EVENTO_SESION_VENCIDA, SESION_URL, decodificarJWT } from './constants';
import { ESTILOS } from './styles';
import { Sidebar, Topbar } from './components/Layout';
import { Dashboard } from './components/Dashboard';
import { CambiosTab } from './components/Cambios';
import { VistaAccesosExternos } from './components/Accesos';
import { VistaCapacitaciones, ModalAltasBajas, ModalRegistroDia } from './components/Capacitaciones';
import { VistaBajas } from './components/Bajas';
import { EmpleadosTabla, ModalEmpleado, ModalExpediente } from './components/Empleados';
import { Comedor } from './components/Comedor';
import { borrarBorradoresComedor } from './components/comedorPedidos';
import { Reclutamiento } from './components/Reclutamiento';
import { ImportacionIntellisis } from './components/ImportacionIntellisis';
import { Evaluaciones } from './components/Evaluaciones';
import { useEsMobile } from './mobile/useEsMobile';
import { LoginMobile } from './mobile/LoginMobile';
import { MobileShell } from './mobile/MobileShell';

export default function PortalRH() {
  const [user, setUser] = useState(null);
  const [errorAcceso, setErrorAcceso] = useState('');
  const [verificandoAcceso, setVerificandoAcceso] = useState(false);
  const [seccionActiva, setSeccionActiva] = useState('dashboard');
  const [subeMenuAbierto, setSubeMenuAbierto] = useState({});
  const [empleados, setEmpleados] = useState([]);
  const [cambios, setCambios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [busquedaActivos, setBusquedaActivos] = useState('');
  // busquedaBajas movido a VistaBajas (maneja su propio estado)
  const [expedienteEmpleado, setExpedienteEmpleado] = useState(null);
  const [modalRegistro, setModalRegistro] = useState(false);
  const [altasBajasAbierto, setAltasBajasAbierto] = useState(false);
  // M.1: misma sesión y lógica; sólo cambia la experiencia según el ancho (ver src/mobile/useEsMobile.js).
  const esMobile = useEsMobile();

  useEffect(() => {
    if (!user) return;
    cargarDatos();
    const interval = setInterval(cargarDatos, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const cargarDatos = async () => {
    try {
      const resEmp = await fetch(API_URL + '?path=api/empleados');
      const datEmp = await resEmp.json();
      const resCam = await fetch(API_URL + '?path=api/cambios-intellisis');
      const datCam = await resCam.json();

      setEmpleados(datEmp.empleados || []);
      setCambios(datCam.cambios || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSubMenu = (id) => {
    setSubeMenuAbierto(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleGoogleLogin = async (credentialResponse) => {
    try {
      const perfil = decodificarJWT(credentialResponse.credential);
      const email = perfil.email || '';
      setErrorAcceso('');

      // El servidor decide: verifica la sesión de Google y aplica dominio corporativo o CAT_ACCESOS.
      setVerificandoAcceso(true);
      try {
        const res = await fetch(SESION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken: credentialResponse.credential })
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setUser({ email: data.email, name: perfil.name, photoUrl: perfil.picture, externo: data.externo, idToken: credentialResponse.credential });
        } else if (res.status === 401) {
          setErrorAcceso(`Acceso denegado: ${email} no está autorizado. Contacta a RH.`);
        } else {
          setErrorAcceso('Error de conexión al verificar acceso. Intenta de nuevo.');
        }
      } catch {
        setErrorAcceso('Error de conexión al verificar acceso. Intenta de nuevo.');
      } finally {
        setVerificandoAcceso(false);
      }
    } catch (error) {
      console.error('Error procesando inicio de sesión:', error);
      setErrorAcceso('No se pudo verificar tu cuenta de Google. Intenta de nuevo.');
    }
  };

  // La sesión del servidor dura un turno: al vencer, cualquier llamada responde 401 y se vuelve al inicio de sesión.
  useEffect(() => {
    const vencida = () => { setUser(null); setErrorAcceso('Tu sesión venció. Inicia sesión de nuevo.'); };
    window.addEventListener(EVENTO_SESION_VENCIDA, vencida);
    return () => window.removeEventListener(EVENTO_SESION_VENCIDA, vencida);
  }, []);

  const handleGoogleLoginError = () => {
    setErrorAcceso('No se pudo iniciar sesión con Google. Intenta de nuevo.');
  };

  const handleLogout = () => {
    borrarBorradoresComedor();   // C.4b: el borrador de Comedor no sobrevive al cierre de sesión
    fetch(SESION_URL, { method: 'DELETE' }).catch(() => {});
    setUser(null);
    setSeccionActiva('dashboard');
  };

  if (!user && esMobile) {
    return <LoginMobile onLogin={handleGoogleLogin} onError={handleGoogleLoginError} error={errorAcceso} verificando={verificandoAcceso} />;
  }

  if (!user) {
    return <PantallaLogin onLogin={handleGoogleLogin} onError={handleGoogleLoginError} error={errorAcceso} verificando={verificandoAcceso} />;
  }

  if (esMobile) {
    return <MobileShell usuario={user} onLogout={handleLogout} empleados={empleados} cambios={cambios} cargandoEmpleados={loading} />;
  }

  return (
    <div className="app">
      <style>{ESTILOS}</style>

      <Sidebar
        seccionActiva={seccionActiva}
        setSeccionActiva={setSeccionActiva}
        subeMenuAbierto={subeMenuAbierto}
        toggleSubMenu={toggleSubMenu}
        usuario={user}
        onLogout={handleLogout}
      />

      <div className="main">
        <Topbar usuario={user} />

        <div className="content">
          {seccionActiva === 'dashboard' && (
            <Dashboard empleados={empleados} cambios={cambios} loading={loading} />
          )}

          {seccionActiva === 'empleados-activos' && (
            <EmpleadosTabla
              empleados={empleados.filter(e => e.Estatus === 'Activo')}
              titulo="Empleados Activos"
              busqueda={busquedaActivos}
              setBusqueda={setBusquedaActivos}
              onNuevo={() => setModalAbierto(true)}
              onVerExpediente={setExpedienteEmpleado}
              onRegistro={() => setModalRegistro(true)}
              onAltasBajas={() => setAltasBajasAbierto(true)}
            />
          )}

          {seccionActiva === 'empleados-bajas' && (
            <VistaBajas onVerExpediente={setExpedienteEmpleado} />
          )}

          {seccionActiva === 'cambios-pendientes' && (
            <CambiosTab
              cambios={cambios.filter(c => c.estatus === 'Pendiente_Intellisis')}
              titulo="Cambios Pendientes"
              onConfirmar={cargarDatos}
            />
          )}

          {seccionActiva === 'cambios-confirmados' && (
            <CambiosTab
              cambios={cambios.filter(c => c.estatus === 'Aplicado_Intellisis')}
              titulo="Cambios Confirmados"
              onConfirmar={cargarDatos}
            />
          )}

          {seccionActiva === 'capacitaciones' && (
            <VistaCapacitaciones />
          )}

          {seccionActiva === 'accesos' && (
            <VistaAccesosExternos />
          )}

          {seccionActiva === 'comedor' && (
            <Comedor usuario={user} />
          )}

          {seccionActiva === 'reclutamiento' && (
            <Reclutamiento />
          )}

          {seccionActiva === 'importacion-intellisis' && (
            <ImportacionIntellisis />
          )}

          {seccionActiva === 'evaluaciones' && (
            <Evaluaciones idToken={user.idToken} />
          )}
        </div>
      </div>

      {modalAbierto && (
        <ModalEmpleado
          empleados={empleados}
          onClose={() => setModalAbierto(false)}
          onCrear={async () => {
            await cargarDatos();
            setModalAbierto(false);
          }}
        />
      )}

      {expedienteEmpleado && (
        <ModalExpediente
          empleado={expedienteEmpleado}
          cambios={cambios.filter(c => String(c.id_empleado) === String(expedienteEmpleado.id))}
          onClose={() => setExpedienteEmpleado(null)}
          onBaja={async () => {
            await cargarDatos();
            setExpedienteEmpleado(null);
          }}
        />
      )}

      {modalRegistro && (
        <ModalRegistroDia onClose={() => setModalRegistro(false)} />
      )}

      {altasBajasAbierto && (
        <ModalAltasBajas onClose={() => setAltasBajasAbierto(false)} />
      )}
    </div>
  );
}

function PantallaLogin({ onLogin, onError, error, verificando }) {
  return (
    <div style={{ display: 'flex', height: '100vh', background: '#f0f4f8' }}>
      {/* Panel izquierdo decorativo */}
      <div style={{
        flex: '0 0 45%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        background: 'linear-gradient(160deg, #1d3d6b 0%, #0086ab 100%)',
        padding: '48px', color: 'white', position: 'relative', overflow: 'hidden'
      }}>
        {/* Círculos decorativos */}
        <div style={{ position: 'absolute', width: '300px', height: '300px', borderRadius: '50%', border: '60px solid rgba(255,255,255,0.06)', top: '-80px', left: '-80px' }} />
        <div style={{ position: 'absolute', width: '200px', height: '200px', borderRadius: '50%', border: '40px solid rgba(255,255,255,0.05)', bottom: '60px', right: '-60px' }} />
        <div style={{ position: 'absolute', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)', bottom: '200px', left: '30px' }} />

        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          {/* Logo */}
          <div style={{ width: '120px', height: '120px', borderRadius: '20px', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', padding: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <img src="/logo-sigrh.png" alt="SIGRH" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <div style={{ fontSize: '13px', letterSpacing: '0.2em', textTransform: 'uppercase', opacity: 0.7, marginBottom: '8px' }}>Aceros y Laminados Leal</div>
          <div style={{ fontSize: '38px', fontWeight: 800, letterSpacing: '-1px', marginBottom: '12px', lineHeight: 1 }}>SIGRH</div>
          <div style={{ fontSize: '15px', opacity: 0.8, fontWeight: 400, marginBottom: '48px', lineHeight: 1.5 }}>Sistema Integral de<br />Recursos Humanos</div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {[['ti-users', 'Gestión de expedientes'], ['ti-file-description', 'Documentos y contratos'], ['ti-chart-bar', 'Control de incidencias']].map(([icon, txt]) => (
              <div key={txt} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(255,255,255,0.08)', borderRadius: '10px', padding: '12px 16px', textAlign: 'left' }}>
                <i className={`ti ${icon}`} style={{ fontSize: '20px', flexShrink: 0 }}></i>
                <span style={{ fontSize: '13px', opacity: 0.9 }}>{txt}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Panel derecho — formulario */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
        <div style={{ width: '100%', maxWidth: '380px' }}>
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '26px', fontWeight: 700, color: '#1d3d6b', marginBottom: '8px' }}>Bienvenido</h2>
            <p style={{ color: '#6b7280', fontSize: '14px' }}>Inicia sesión con tu cuenta corporativa para continuar.</p>
          </div>

          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' }}>
            <p style={{ fontSize: '12px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '16px' }}>Acceso restringido</p>

            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 14px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="ti ti-lock" style={{ fontSize: '18px', color: '#64748b' }}></i>
              <span style={{ fontSize: '13px', color: '#64748b' }}>Acceso para personal autorizado por RH</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              {verificando
                ? <div style={{ padding: '14px 0', color: '#64748b', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <i className="ti ti-loader-2" style={{ fontSize: '20px' }}></i>
                    Verificando acceso...
                  </div>
                : <GoogleLogin onSuccess={onLogin} onError={onError} size="large" width="280" />
              }
            </div>

            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', marginTop: '12px' }}>
                <p style={{ color: '#dc2626', fontSize: '13px', fontWeight: 600, margin: 0 }}>⚠️ {error}</p>
              </div>
            )}
          </div>

          <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '12px', color: '#9ca3af' }}>
            ¿Problemas de acceso? Contacta a TI.
          </p>
        </div>
      </div>
    </div>
  );
}
